import { useCallback, useMemo, useRef } from 'react';

import { useStatic } from '../../../../hooks/useStatic';
import { persistentKeyValueStore } from '../../../../services/persistentKeyValueStore';
import { PersistentStorageContextProps } from '../../types/PersistentStorageContextProps';

export const usePersistentStorageManagement = (
  namespace: PersistentStorageContextProps['namespace'],
  allowedKeys: PersistentStorageContextProps['allowedKeys'],
) => {
  const initialAllowedKeys = useRef(allowedKeys);

  const persistentStore = useStatic(() => persistentKeyValueStore(namespace));

  const clearAllKeys = useCallback(() => {
    initialAllowedKeys.current.forEach((key) => {
      persistentStore.deleteValueForKey(key);
    });
  }, [persistentStore]);

  const getItem = useCallback(
    <T>(key: string, defaultValue?: T) => {
      if (!initialAllowedKeys.current.includes(key)) {
        throw new Error(`Key ${key} is not allowed`);
      }
      return persistentStore.getValueForKey(key, defaultValue) as T | undefined;
    },
    [persistentStore],
  );

  const setItem = useCallback(
    (key: string, value: unknown) => {
      if (!initialAllowedKeys.current.includes(key)) {
        throw new Error(`Key ${key} is not allowed`);
      }

      if (value !== undefined) {
        persistentStore.setValueForKey(key, value);
      } else {
        persistentStore.deleteValueForKey(key);
      }
    },
    [persistentStore],
  );

  const apiValue = useMemo(
    () => ({
      clearAllKeys,
      getItem,
      setItem,
    }),
    [clearAllKeys, getItem, setItem],
  );

  return {
    apiValue,
  };
};
